import { api } from "./api";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

export interface User {
  _id: string;
  email: string;
  username: string;
  firstName?: string;
  lastName?: string;
  role: string;
  avatar?: string;
  bio?: string;
  phoneNumber?: string;
  location?: string;
  socialLinks?: {
    website?: string;
    linkedin?: string;
    github?: string;
    twitter?: string;
    facebook?: string;
  };
  isVerified: boolean;
  createdAt: string;
  isActive?: boolean;
  isBlocked?: boolean;
  isSuspended?: boolean;
  lastLogin?: string;
  coursesCount?: number;
  completeCourses?: number;
  totalHours?: number;
  requires2FA?: boolean;
}

export const authApi = {
  login: (email: string, password: string, location?: string) =>
    api.post<{ success: boolean; user: User; accessToken: string; requires2FA?: boolean }>(
      "/auth/login",
      { email, password },
      location ? { headers: { "x-location": location } } : undefined,
    ),

  verifyLogin2FA: (email: string, code: string) =>
    api.post<{ success: boolean; accessToken: string }>("/auth/2fa/verify-login", { email, code }),

  register: (data: {
    username: string;
    email: string;
    password: string;
    role?: string;
    firstName?: string;
    lastName?: string;
  }) =>
    api.post<{ success: boolean; message: string }>("/auth/register", data),

  logout: () => api.post<{ success: boolean }>("/auth/logout"),

  verifyEmail: (email: string, code: string) =>
    api.post<{ success: boolean; message: string }>("/auth/verify-email", {
      email,
      code,
    }),

  resendVerification: (email: string) =>
    api.post<{ success: boolean; message: string }>(
      "/auth/resend-verification",
      { email }
    ),

  forgotPassword: (email: string) =>
    api.post<{ success: boolean; message: string }>(
      "/auth/forgot-password",
      { email }
    ),

  resetPassword: (token: string, newPassword: string, confirmPassword: string) =>
    api.post<{ success: boolean; message: string }>(
      "/auth/reset-password",
      { token, newPassword, confirmPassword }
    ),

  refreshToken: () =>
    api.post<{ success: boolean; accessToken: string }>(
      "/auth/refresh-token"
    ),

  getCurrentUser: () => api.get<{ success: boolean; data: User }>("/users/me"),

  updateProfile: (data: {
    firstName?: string;
    lastName?: string;
    username?: string;
    bio?: string;
    avatar?: string;
    phoneNumber?: string;
    location?: string;
    socialLinks?: {
      website?: string;
      linkedin?: string;
      github?: string;
      twitter?: string;
    };
  }) => api.put<{ success: boolean; message: string; data: User }>("/users/profile", data),

  changePassword: (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => api.put<{ success: boolean; message: string }>("/users/change-password", data),

  deleteAccount: () => api.delete<{ success: boolean; message: string }>("/users/profile"),

  uploadAvatar: async (file: File) => {
    // Try file upload first
    try {
      return await api.uploadFile<{ success: boolean; message: string; data: { url: string } }>("/upload/avatar", file, "avatar");
    } catch (error: any) {
      // Fallback to base64 if file upload fails
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
      
      const imageData = base64.split(",")[1];
      return api.post<{ success: boolean; message: string; data: { url: string } }>("/upload/avatar/base64", { image: imageData });
    }
  },

  // 2FA - Email based
  enable2FA: () =>
    api.post<{ success: boolean; message: string }>("/auth/2fa/enable", {}),

  verify2FA: (code: string) =>
    api.post<{ success: boolean; message: string }>("/auth/2fa/verify", { code }),

  disable2FA: (password: string) =>
    api.post<{ success: boolean; message: string }>("/auth/2fa/disable", { password }),

  send2FACode: (email: string) =>
    api.post<{ success: boolean; message: string }>("/auth/2fa/send-login-code", { email }),

  getActiveSessions: () =>
    api.get<{ success: boolean; data: Array<{ id: string; device: string; browser: string; os: string; ip: string; location: string; loginAt: string }> }>("/auth/sessions"),

  revokeSession: (sessionId: string) =>
    api.delete<{ success: boolean; message: string }>(`/auth/sessions/${sessionId}`),

  getGoogleAuthUrl: async (): Promise<string> => {
    const response = await api.get<{ success: boolean; url: string }>("/auth/google");
    return response.url;
  },

  loginWithGoogle: async (): Promise<void> => {
    try {
      const url = await authApi.getGoogleAuthUrl();
      window.location.href = url;
    } catch (error) {
      console.error("Google auth URL error:", error);
    }
  },

  handleGoogleCallback: (token: string, userData: User) => {
    localStorage.setItem("accessToken", token);
    localStorage.setItem("user", JSON.stringify(userData));
  },

  getGoogleClientId: () => GOOGLE_CLIENT_ID,
};